/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  WHERE THE TOKENS LIVE. ONE FILE, ON PURPOSE.
 *
 *  This project uses NO COOKIES: both tokens are returned in the response body and the
 *  client holds them. The access token lives in `sessionStorage` (30 minutes) and the
 *  refresh token in `localStorage`, so a reload — and a browser restart — keeps the session.
 *  ⚠️ [amended SESSION-TTL] The session lasts 30 days on the officer surface (Duty Mitra) and
 *  1 day on the dashboard, counted from the OTP sign-in — the server decides it, and nothing in
 *  this file reads or schedules against it; a refresh past the end is the ordinary 401.
 *
 *  ⚠️ THIS IS A DELIBERATE DEPARTURE FROM CLAUDE.md §8, AND IT HAS A COST.
 *
 *  §8 specifies an httpOnly SameSite cookie precisely because JavaScript cannot read
 *  one. A refresh token in `localStorage` IS readable by any script that gets onto the
 *  page, so an XSS becomes a session takeover for the rest of the session rather than a
 *  30-minute one — ⚠️ [amended SESSION-TTL] up to 30 DAYS on Duty Mitra now, where it was 36
 *  hours, and one day on the dashboard. The decision was taken with that trade explicit — see
 *  CHANGELOG P1a and [SESSION-TTL].
 *
 *  What makes it survivable is the compensating control on the server: session state
 *  lives in Redis, so a token can actually be REVOKED. Refresh rotation with family
 *  reuse detection means a stolen token used alongside the real one kills the whole
 *  chain, and `logout` with `allSessions` ends every session everywhere. None of that
 *  is possible with stateless JWTs, which is why it is not optional here.
 *
 *  Everything else that protects this: no `dangerouslySetInnerHTML` anywhere (ESLint
 *  blocks it), a strict CSP from helmet, and a 30-minute access token so the blast
 *  radius of the SHORT-lived credential stays small.
 *
 *  If this ever moves back to a cookie, it moves HERE and nowhere else — which is the
 *  reason this file exists rather than two `localStorage` calls inlined at the call
 *  sites that need them.
 *
 *  ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [amended P5b] THE KEYS ARE A PARAMETER, AND THE STORE IS A FACTORY. BOTH MATTER.
 *
 *  THE KEYS. Two apps now hold sessions — the dashboard and the officer PWA — and their
 *  tokens are minted for DIFFERENT AUDIENCES (`pams:dashboard` and `pams:officer`).
 *  Sharing one storage slot would put an officer token where a dashboard token belongs
 *  the moment both are opened on one origin, and the API would then answer every request
 *  with `403 WRONG_SURFACE` while the client refreshed a perfectly valid token for ever.
 *  Different ports make that unlikely today and a path-prefix deployment would make it
 *  certain; a keyed store makes it impossible either way. `web` keeps `pams.at`/`pams.rt`
 *  EXACTLY — they are asserted literally in `web/src/lib/api.test.ts` and hardcoded in
 *  `e2e/fixtures/session.ts`.
 *
 *  THE FACTORY. This module used to hold `accessToken`/`refreshToken` as module-level
 *  mutable state. That is correct in an app that resolves the module once and CATASTROPHIC
 *  in a package that might resolve twice — two memory mirrors, two single-flight mutexes,
 *  and a refresh storm that the server correctly reads as token reuse and answers by
 *  revoking the family. Making the state an instance makes "one per app" the shape of the
 *  thing rather than a property of module resolution that nothing checks.
 *
 *  ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [amended C1a] THE REFRESH TOKEN IS READ-THROUGH. THE ACCESS TOKEN IS NOT. THE
 *  ASYMMETRY IS THE FIX, NOT AN OVERSIGHT.
 *
 *  `getRefreshToken` was `refreshToken ??= safeRead(localStorage, refreshKey)` — a mirror
 *  hydrated ONCE and authoritative for the life of the tab. `localStorage` is shared
 *  between tabs, so tab B's rotation wrote a successor that tab A could never see; tab A
 *  went on presenting the parent, and a parent presented after its successor exists is
 *  INDISTINGUISHABLE FROM A REPLAY. The server did the right thing and revoked the
 *  family. That is C1a §1 — users signed out after a few minutes of ordinary two-tab use.
 *
 *  So the refresh credential is now re-read from `localStorage` on every call, and `null`
 *  there is AUTHORITATIVE: another tab signed out, and that must propagate.
 *
 *  ⚠️ `getAccessToken` STAYS ON `??=`, AND THE REASON IS THAT THE TWO STALENESSES COST
 *  DIFFERENT THINGS. `sessionStorage` is per tab, so there is no second writer for a
 *  read-through to reconcile; `getAccessToken` runs on EVERY request, so a read-through
 *  would put a synchronous storage call on the hot path for no information; a stale
 *  access token self-heals in one 401 → refresh → retry that `http/apiClient.ts` already
 *  performs and `web/src/lib/api.test.ts` already pins, whereas a stale refresh token
 *  costs the whole family. Self-healing versus catastrophic is the whole argument.
 *
 *  ⚠️ AND "STORAGE SAID NULL" IS NO LONGER THE SAME ANSWER AS "STORAGE THREW". They were
 *  collapsed into `undefined`, which a read-through cannot afford: null must sign this tab
 *  out, a throw must fall back to the mirror. `refreshStorageUsable` is the third case —
 *  Safari private browsing lets `getItem` succeed while `setItem` throws, so a store whose
 *  own write never landed must keep trusting its mirror or it would sign the user out
 *  INSIDE THEIR OWN TAB, which is a regression this file would otherwise have shipped.
 *
 *  ⚠️ THE STORAGE AREA IS A THUNK, AND THAT FIXED A LIVE BUG. `safeRead(localStorage, …)`
 *  evaluated `localStorage` IN THE ARGUMENT LIST, outside the `try` — and in a sandboxed
 *  iframe the property access ITSELF throws `SecurityError`, so `getRefreshToken()` threw
 *  in exactly the case this header claimed to survive.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
import { createRefreshLock, type LockRead, type LockStorage, type RefreshLock } from './refreshLock';

export type TokenPair = { accessToken: string; refreshToken: string };

export type TokenStore = {
  getAccessToken: () => string | undefined;
  getRefreshToken: () => string | undefined;
  setTokens: (tokens: TokenPair) => void;
  clearTokens: () => void;
  /**
   * ⚠️ [C1a] Serialises the refresh rotation ACROSS tabs. `apiClient`'s `refreshInFlight`
   * only ever covers one. See `refreshLock.ts` for why it is a lease and not a Web Lock.
   */
  refreshLock: RefreshLock;
  /**
   * ─── ⚠️ [C1b §6a] THE IDEMPOTENCY KEY OF A ROTATION THAT IS STILL IN FLIGHT ───────
   *
   * PERSISTED, and only for the life of one rotation. `setTokens` deletes it, so at any
   * SETTLED instant `localStorage` holds a refresh token and NO key — which is what keeps
   * reuse detection exactly as strong as it was: a point-in-time storage snapshot yields
   * `(token, ø)` and a replay built from it is caught.
   *
   * ⚠️ **IN-MEMORY WAS THE RUNNER-UP AND IT CLOSES THE WRONG HALF.** It survives a fetch
   * timeout inside one closure and nothing else — not a reload, not an OS app-kill, which is
   * precisely the poor-signal handset §6a is about.
   *
   * ⚠️ **THE KEY IS DERIVED FROM `refreshKey`**, so no new `pams.*` literal appears and
   * `clientCoreIsolation.test.ts` CLAIM 2's exact four-site list is untouched.
   */
  getPendingRotationKey: () => string | undefined;
  setPendingRotationKey: (key: string) => void;
  /**
   * ⚠️ [C1a] Fires when ANOTHER context removes this store's refresh token — i.e. somebody
   * signed out in a second tab. Returns an unsubscribe. It is an ACCELERATOR, not the
   * correctness path: the read-through above already reports the removal at the next call.
   */
  onExternalClear: (handler: () => void) => () => void;
};

export type TokenStoreOptions = {
  /** `sessionStorage` key for the 30-minute access token. */
  accessKey: string;
  /** `localStorage` key for the refresh token — a 30-day session on Duty Mitra, one day on the dashboard. */
  refreshKey: string;
};

/**
 * Storage can throw: Safari private browsing, a locked-down enterprise profile, or a
 * sandboxed iframe. A failure to persist must degrade to "you are signed out on the
 * next reload", never to a blank screen.
 *
 * ⚠️ `area` IS A THUNK. Naming the global in the argument list puts the property access
 * outside the `try`, and in a sandboxed iframe that access is itself what throws.
 */
const readEntry = (area: () => Storage, key: string): LockRead => {
  try {
    return { ok: true, value: area().getItem(key) };
  } catch {
    return { ok: false };
  }
};

/**
 * Returns whether the value is RETRIEVABLE afterwards, not merely that `setItem` did not
 * throw — a quota-full or sandboxed area can accept a write and drop it, and the
 * read-through above must not treat that silence as a sign-out.
 */
const writeEntry = (area: () => Storage, key: string, value: string | undefined): boolean => {
  try {
    if (value === undefined) {
      area().removeItem(key);
      return true;
    }
    area().setItem(key, value);
    // Non-null rather than `=== value`: a foreign write racing us is still evidence that
    // this area works, which is the only thing being established here.
    return area().getItem(key) !== null;
  } catch {
    return false;
  }
};

const local = (): Storage => localStorage;
const session = (): Storage => sessionStorage;

/** One listener per store, filtered. `key === null` is a `Storage.clear()` from elsewhere. */
const subscribeToKey = (key: string, onChange: (event: StorageEvent) => void): (() => void) => {
  if (typeof window === 'undefined') return () => undefined;
  const listener = (event: StorageEvent): void => {
    if (event.key === key || event.key === null) onChange(event);
  };
  window.addEventListener('storage', listener);
  return () => {
    window.removeEventListener('storage', listener);
  };
};

export const createTokenStore = ({ accessKey, refreshKey }: TokenStoreOptions): TokenStore => {
  /**
   * A memory mirror, so a request issued before the first read — or in an environment
   * with no storage at all — still carries the token.
   *
   * ⚠️ [C1a] For the ACCESS token it is the hydrated source of truth (per tab, one
   * writer). For the REFRESH token it is a FALLBACK ONLY, consulted when the area throws
   * or when this store's own write never landed. See the header.
   */
  let accessToken: string | undefined;
  let refreshToken: string | undefined;
  /**
   * Latched false only by a write that did not land, and reset by `clearTokens`. It is
   * what keeps Safari private browsing — `getItem` works, `setItem` throws — from reading
   * as "another tab signed me out" on the very next call.
   */
  let refreshStorageUsable = true;
  /** ⚠️ [C1b §6a] Derived, never a literal — see `refreshLock`'s key below for the reason. */
  const rotationKeyKey = `${refreshKey}.rk`;

  const lockStorage: LockStorage = {
    read: (key) => readEntry(local, key),
    write: (key, value) => writeEntry(local, key, value),
    remove: (key) => {
      writeEntry(local, key, undefined);
    },
    subscribe: (key, onChange) => subscribeToKey(key, () => onChange()),
  };

  return {
    getAccessToken: (): string | undefined => {
      if (accessToken !== undefined) return accessToken;
      const read = readEntry(session, accessKey);
      accessToken = read.ok ? (read.value ?? undefined) : undefined;
      return accessToken;
    },

    getRefreshToken: (): string | undefined => {
      if (!refreshStorageUsable) return refreshToken;
      const read = readEntry(local, refreshKey);
      // Threw on THIS call: the area is momentarily unreachable, so answer from the
      // mirror and do NOT latch — a transient failure is not a sign-out.
      if (!read.ok) return refreshToken;
      refreshToken = read.value ?? undefined;
      return refreshToken;
    },

    getPendingRotationKey: (): string | undefined => {
      const read = readEntry(local, rotationKeyKey);
      return read.ok ? (read.value ?? undefined) : undefined;
    },

    setPendingRotationKey: (key: string): void => {
      writeEntry(local, rotationKeyKey, key);
    },

    setTokens: (tokens: TokenPair): void => {
      accessToken = tokens.accessToken;
      refreshToken = tokens.refreshToken;
      writeEntry(session, accessKey, tokens.accessToken);
      refreshStorageUsable = writeEntry(local, refreshKey, tokens.refreshToken);
      /*
       * ⚠️ [C1b §6a] THE ROTATION LANDED, SO THE KEY GOES — IN THE SAME FUNCTION, NOT IN A
       * `finally` SOMEWHERE ELSE. This is what makes "a settled store holds a token and no
       * key" a property of the storage rather than a promise about a code path.
       */
      writeEntry(local, rotationKeyKey, undefined);
    },

    clearTokens: (): void => {
      accessToken = undefined;
      refreshToken = undefined;
      // Reset rather than leave latched: the next write decides again, and a store that
      // signed out is not a store whose storage is known broken.
      refreshStorageUsable = true;
      writeEntry(session, accessKey, undefined);
      writeEntry(local, refreshKey, undefined);
      writeEntry(local, rotationKeyKey, undefined);
    },

    /**
     * ⚠️ THE KEY IS DERIVED, NEVER A LITERAL. A literal would be a fifth site naming
     * `pams.*` for `clientCoreIsolation.test.ts` CLAIM 2 to trip over, and deriving it is
     * also what keeps the two apps' locks apart: `pams.rt.lock` and `pams.app.rt.lock`
     * are two different families and must never serialise against each other.
     */
    refreshLock: createRefreshLock({
      key: `${refreshKey}.lock`,
      storage: lockStorage,
      onUnload: (handler) => {
        if (typeof window === 'undefined') return () => undefined;
        window.addEventListener('pagehide', handler);
        return () => {
          window.removeEventListener('pagehide', handler);
        };
      },
    }),

    onExternalClear: (handler: () => void): (() => void) =>
      subscribeToKey(refreshKey, (event) => {
        // `newValue === null` is a removal; `key === null` is a whole-area `clear()`.
        if (event.newValue === null) handler();
      }),
  };
};
